/**
 * Publish-time topology rules for the Await (`fan_in`) step.
 *
 * `fan_in` is the only step whose meaning depends on where it sits rather than
 * on how it is configured. It is the join half of fanout, so it is only
 * well-defined when it ends exactly one fanned-out region:
 *
 *  - **No region upstream** — nothing ever forked, so there is nothing to
 *    join. The engine would never reach the step through its join site, and
 *    the readiness loop cannot satisfy its input either.
 *  - **More than one region upstream** — either two independent fanouts
 *    converge on the same Await, or a nested fanout runs inside the region.
 *    The barrier would have no single cohort to gather, and the orchestrator
 *    refuses both at runtime; catching them here is what makes that refusal a
 *    design-time message instead of a failed run.
 *  - **A detached region upstream** — the fanout source declares
 *    `detachedFanout`, so its children are fire-and-forget: the parent never
 *    sees a result to gather. The orchestrator throws before spawning any of
 *    them, which would fail every run of an otherwise publishable graph.
 *    "Upstream" here means reachable by ANY edge, not just the Value wire —
 *    that is the reachability `runDetachedFanout` itself refuses on.
 *  - **`T` unresolved** — `fan_in` is generic (`input<T> → array<T>`) and is
 *    monomorphized from the wire feeding `value`. Until that wire exists the
 *    node has no result socket, and emitting an untyped one instead would let
 *    an incompatible downstream wire pass validation and fail at runtime.
 *
 * These are blocking errors, not warnings: unlike the fanout-shape findings
 * next door, a graph that breaks one of these does not run correctly at a
 * smaller scale — it does not run at all.
 *
 * Design reference: `docs/workflow-closures-design.md` § "`fan_in` — the gather
 * / join step".
 */

import {
  FAN_IN_INPUT_SOCKET_ID,
  FAN_IN_RESULT_SOCKET_ID,
  FAN_IN_RESULTS_SOCKET_ID,
  FAN_IN_STEP_TYPE,
  isFirstMode,
  resolveFanInMode,
} from "../utils/dynamicSockets/fanInOutputSockets";
import { resolveActiveEdges } from "../expressions";
import { isFlowSocket } from "../types";
import { declaresDetachedFanout } from "./detached-fanout";
import { analyseFanoutTopology } from "./fanout-topology";
import type { ValidationEdge, ValidationError, ValidationStep } from "./types";

export function isFanInValidationStep(step: ValidationStep): boolean {
  return step.type === FAN_IN_STEP_TYPE;
}

/** Canvas label for user-facing messages, falling back to type then id. */
const nameOf = (step: ValidationStep): string =>
  step.name ?? step.type ?? step.id;

/**
 * Every step reachable from `startStepId` by following edges of any kind.
 *
 * Deliberately NOT the value path the region checks walk. `runDetachedFanout`
 * seeds the step ids its children own from `findDownstreamSteps` over the
 * active edges — flow edges included — and throws when an Await is among them.
 * Walking value wires alone would miss exactly the graph the engine refuses: a
 * detached region reaching the join through a flow edge while a separate,
 * ordinary region supplies the Value.
 *
 * The caller passes EXPRESSION-RESOLVED edges, not the stored ones, for the
 * same reason `analyseFanoutTopology` traverses them: an expression-backed
 * input replaces its stored edge, so the two sets disagree about what is
 * downstream exactly when a socket is overridden. The engine walks the resolved
 * set, and a check on the stored set would clear a graph the engine refuses.
 */
function collectDownstream(
  startStepId: string,
  edges: Array<{ sourceStepId: string; targetStepId: string }>,
): Set<string> {
  const reached = new Set<string>();
  const walk = (stepId: string) => {
    for (const edge of edges) {
      if (edge.sourceStepId !== stepId) continue;
      if (reached.has(edge.targetStepId)) continue;
      reached.add(edge.targetStepId);
      walk(edge.targetStepId);
    }
  };
  walk(startStepId);
  return reached;
}

/**
 * Validate ONE Await: its Value wire, the region that wire comes from, and the
 * result socket that region's element type produced.
 *
 * Returns the regions it was found to end (at most one when valid) so the
 * caller can check the other direction of the one-to-one rule.
 */
// The branch count IS the rule set: each arm is one topology the engine refuses
// at run time, with one named test in fan-in-topology.test.ts. Splitting them
// into helpers would scatter the list this function exists to be readable as,
// without removing a single branch. Everything after the kind on the line below
// is parsed as further issue kinds, so the reason lives up here.
// fallow-ignore-next-line complexity
function checkOneFanIn(
  fanInStep: ValidationStep,
  steps: ValidationStep[],
  edges: ValidationEdge[],
  /** Expression-resolved traversal set — what the engine actually walks. */
  traversalEdges: Array<{
    sourceStepId: string;
    sourceSocketId: string;
    targetStepId: string;
    targetSocketId: string;
  }>,
  findings: ReturnType<typeof analyseFanoutTopology>,
  options: FanInTopologyOptions,
): { errors: ValidationError[]; enclosingTargetIds: Set<string> } {
  const errors: ValidationError[] = [];
  const label = nameOf(fanInStep);

  // The region test keys on the sources of the VALUE edge, not on every
  // incoming edge. An Await joins the region that produces the value it
  // gathers, and only that. Accepting any incoming edge let a mixed topology
  // through — a flow edge from inside the region satisfying "enclosed" while
  // the value came from OUTSIDE it — and at runtime the externally supplied
  // value is already present when the Await is reached, so the parent
  // resolves it inline as a cohort of one and the real harvest is dropped
  // later on the completedSteps check. Same silent data loss as an Await
  // queued early, reached by a different route.
  //
  // Monomorphization needs the same wire, so the unwired case is settled
  // first: with no value edge there is no source to locate a region from,
  // and "nothing is wired into Value" is the actionable message anyway.
  //
  // Read off the EXPRESSION-RESOLVED set, not the stored one. An override on
  // `value` replaces its stored edge, so the two disagree about what feeds the
  // join exactly when someone has overridden it — and the runtime follows the
  // resolved set. On the stored set a valid override was rejected as coming
  // from outside the region, and an Await whose active value comes from a
  // DIFFERENT region published and then gathered the wrong cohort. It also
  // fixes the unwired report: an expression-only socket is wired.
  const valueFeederStepIds = new Set(
    traversalEdges
      .filter(
        (edge) =>
          edge.targetStepId === fanInStep.id &&
          edge.targetSocketId === FAN_IN_INPUT_SOCKET_ID,
      )
      .map((edge) => edge.sourceStepId),
  );

  if (valueFeederStepIds.size === 0) {
    errors.push({
      code: "UNCONNECTED_REQUIRED_SOCKET",
      message: `"${label}" (Await) has nothing wired into its Value input, so its result type is unresolved.`,
      nodeIds: [fanInStep.id],
      sockets: [{ stepId: fanInStep.id, socketId: FAN_IN_INPUT_SOCKET_ID }],
    });
    return { errors, enclosingTargetIds: new Set() };
  }

  // Count COHORTS, not findings. `analyseFanoutTopology` reports one finding
  // per fanned socket, so a step with two array inputs yields two findings —
  // but the engine still spawns a single cohort from it: `detectFanout` fans
  // the first fanned key only, and the batch path zips the aligned arrays
  // into N tuples. Both give N children, never N x M. Keying on the fanout
  // TARGET collapses those back into the one region they really are, so a
  // valid multi-input fanout followed by an Await is not rejected. (The
  // nested-fanout check next door already dedupes the same way.)
  const enclosingFindings = findings.filter((finding) =>
    finding.branchStepIds.some((stepId) => valueFeederStepIds.has(stepId)),
  );
  const enclosingTargetIds = new Set(
    enclosingFindings.map((finding) => finding.boundary.targetStepId),
  );

  if (enclosingTargetIds.size === 0) {
    errors.push({
      code: "INVALID_STEP_CONFIG",
      message: `"${label}" (Await) is not downstream of any fanned-out region, so there is nothing for it to join. Wire its Value input from a step inside a region that receives an array on a single-value input.`,
      nodeIds: [fanInStep.id],
    });
    return { errors, enclosingTargetIds };
  }

  if (enclosingTargetIds.size > 1) {
    errors.push({
      code: "INVALID_STEP_CONFIG",
      message: `"${label}" (Await) sits inside ${enclosingTargetIds.size} fanned-out regions, so it has no single cohort to gather. A fan-in must end exactly one region — remove the nested fanout, or give each region its own Await.`,
      nodeIds: [fanInStep.id, ...enclosingTargetIds],
    });
    return { errors, enclosingTargetIds: new Set() };
  }

  // A DETACHED region cannot be joined at all. When the step producing the
  // fanned arrays declares `detachedFanout`, the engine spawns the per-item
  // children as fire-and-forget child workflows and the parent completes once
  // they have STARTED — it never observes a result, so the barrier has no
  // cohort to gather and `runDetachedFanout` throws before spawning anything.
  //
  // Scanned over EVERY finding by reachability, not over `enclosingFindings`.
  // The enclosing set is keyed on the Value wire, which is right for the region
  // rules above but wrong here: the engine's guard fires on whatever lands in
  // `remainingStepIds`, seeded by walking all active edges out of the fanout
  // source. A detached region reaching this Await through a FLOW edge, while an
  // ordinary region supplies its Value, has exactly one enclosing region and so
  // passed every check above — then failed every run.
  //
  // Rejected on the source's flag alone, without also proving the fanned step
  // is batched (the other half of the run-time condition). Batch config lives
  // on the step TYPE, so which of the two paths a graph takes is invisible on
  // the canvas: the same wiring silently either joins fine (awaited fanout) or
  // fails every run (detached). An Await under a detached source is refused
  // outright rather than made to depend on a flag the author cannot see.
  // The candidate is whatever ACTIVELY feeds each fanned socket, which is not
  // the same as the boundary's recorded source. `analyseFanoutTopology` finds
  // boundaries over the UNION of stored and expression edges and claims each
  // target socket once, so when both an edge and an expression land on the same
  // socket the boundary records whichever came first in that union — not
  // whichever the engine will follow.
  //
  // That misattribution runs both ways, and this is a BLOCKING error, so both
  // directions are real bugs: an inert detached edge outvoting the active
  // ordinary source refuses a graph that runs correctly, and an inert ordinary
  // edge outvoting the active detached source publishes one that fails every
  // run. Re-resolving the source from the traversal set answers the question
  // the runtime actually asks.
  // ANY fanned socket whose active source is detached is enough. Matching the
  // engine's `.every` looked right — `fanoutSourceDeclaresDetached` does take
  // all the fanned input keys — but that set is DATA-dependent:
  // `detectLockstepFanout` only fans a key whose runtime array holds more than
  // one item. Give the detached input three rows and the ordinary input one,
  // and the only fanned key is the detached one, `.every` is trivially true,
  // and the run takes the detached path and throws at the join.
  //
  // So a mixed step is not safely acceptable — which path it takes is decided
  // by the widths of two arrays no validator can see. That is the same reason
  // the rule keys on the source's flag rather than on batch config: refusing a
  // shape that only sometimes works beats publishing one whose behaviour is a
  // coin flip on data.
  const detachedSourceStepIds = new Set(
    findings.flatMap((finding) => {
      const activeEdge = traversalEdges.find(
        (edge) =>
          edge.targetStepId === finding.boundary.targetStepId &&
          edge.targetSocketId === finding.boundary.targetSocketId,
      );
      if (!activeEdge) return [];
      return declaresDetachedFanout(
        steps.find((candidate) => candidate.id === activeEdge.sourceStepId)
          ?.type ?? "",
      )
        ? [activeEdge.sourceStepId]
        : [];
    }),
  );

  const detachedSourceStepId = [...detachedSourceStepIds].find((sourceStepId) =>
    collectDownstream(sourceStepId, traversalEdges).has(fanInStep.id),
  );
  if (detachedSourceStepId) {
    const sourceStep = steps.find(
      (candidate) => candidate.id === detachedSourceStepId,
    );
    errors.push({
      code: "INVALID_STEP_CONFIG",
      message: `"${label}" (Await) is downstream of "${sourceStep ? nameOf(sourceStep) : detachedSourceStepId}", which runs its fanned children detached (fire-and-forget). Detached children are never collected, so there is nothing to join — remove the Await, or fan out from a step that awaits its children.`,
      nodeIds: [fanInStep.id, detachedSourceStepId],
    });
    return { errors, enclosingTargetIds: new Set() };
  }

  // A BATCHED region can only be joined at its own step. Batch execution runs
  // the fanned step once per item inside the parent and rejoins the outputs
  // into an aggregate — it never forks the region, so every step AFTER it runs
  // exactly once, on that aggregate. A value edge straight from the batched
  // step can still be unpacked back into per-item outcomes; anything further
  // downstream cannot, because the per-item values stopped existing there.
  //
  // Left to publish, the harvest finds no direct edge, the Await falls back to
  // the inline path, and the aggregate counts as a single child: nested arrays
  // for `all_success`, an aggregate where `first_*` promises one value, and
  // every per-item failure gone from `all_settled`.
  //
  // The declared socket types cannot see this coming. A batched step declares
  // the SINGLE-item shape it handles (`written: boolean`), and the array is a
  // run-time consequence of batching, so the intervening step consuming
  // `boolean` is not a fanout boundary and no second region is reported.
  const boundaryTargetStepId = [...enclosingTargetIds][0];
  const boundaryTargetStep = boundaryTargetStepId
    ? steps.find((candidate) => candidate.id === boundaryTargetStepId)
    : undefined;
  if (
    options.isBatchedStepType &&
    boundaryTargetStep &&
    boundaryTargetStepId &&
    options.isBatchedStepType(boundaryTargetStep.type) &&
    !valueFeederStepIds.has(boundaryTargetStepId)
  ) {
    errors.push({
      code: "INVALID_STEP_CONFIG",
      message: `"${label}" (Await) joins a region fanned into "${nameOf(boundaryTargetStep)}", which runs its items batched — the steps after it run once on the combined result, so by the time the value reaches this Await there is no longer one result per item. Wire the Await's Value straight from "${nameOf(boundaryTargetStep)}".`,
      nodeIds: [fanInStep.id, boundaryTargetStepId],
    });
    return { errors, enclosingTargetIds: new Set() };
  }

  // Nothing may gate the Await from OUTSIDE its region. A harvested cohort
  // satisfies the join on its own, so the engine declares the step ready the
  // moment the harvest exists and never re-runs the readiness check — an
  // unrelated branch wired into the Await's flow input is simply ignored, and
  // downstream work starts before that branch has finished.
  //
  // Refused here rather than repaired in the loop: making the join wait on its
  // other edges means running `isStepReady` on a step whose `value` no ordinary
  // input resolution can satisfy, and whose in-region flow sources are marked
  // `_handledByChildWorkflow` in the parent — a readiness rule that could as
  // easily hang the join as fix it. An edge that does nothing is better refused
  // than reinterpreted.
  const enclosingRegionStepIds = new Set(
    enclosingFindings.flatMap((finding) => finding.branchStepIds),
  );
  const outsideGate = traversalEdges.find(
    (edge) =>
      edge.targetStepId === fanInStep.id &&
      edge.targetSocketId !== FAN_IN_INPUT_SOCKET_ID &&
      !enclosingRegionStepIds.has(edge.sourceStepId),
  );
  if (outsideGate) {
    const gateStep = steps.find(
      (candidate) => candidate.id === outsideGate.sourceStepId,
    );
    errors.push({
      code: "INVALID_STEP_CONFIG",
      message: `"${label}" (Await) has an incoming edge from "${gateStep ? nameOf(gateStep) : outsideGate.sourceStepId}", which is outside the region it joins. An Await is released by its own cohort, so an edge from anywhere else never holds it back — the work downstream would start without waiting. Remove the edge, or move that step inside the fanned region.`,
      nodeIds: [fanInStep.id, outsideGate.sourceStepId],
    });
    return { errors, enclosingTargetIds: new Set() };
  }

  // Nothing past the join may read a region step's output directly. The join
  // EXILES everything downstream of itself from the region
  // (`collectFannedRegion`), so those steps run once in the parent — where every
  // region step is stamped `{_handledByChildWorkflow: true}` and has no socket
  // values at all. The child that computed the real value has already closed,
  // and its output only ever reaches the parent through the Await's gather.
  //
  // So the wire looks ordinary on the canvas and resolves to nothing at run
  // time. Data edges only: a flow edge carries no value, and ordering across
  // the join is the Await's own job.
  const postJoinStepIds = collectDownstream(fanInStep.id, traversalEdges);
  const crossing = traversalEdges.find(
    (edge) =>
      !isFlowSocket(edge.targetSocketId) &&
      enclosingRegionStepIds.has(edge.sourceStepId) &&
      postJoinStepIds.has(edge.targetStepId),
  );
  if (crossing) {
    const fromStep = steps.find((s) => s.id === crossing.sourceStepId);
    const toStep = steps.find((s) => s.id === crossing.targetStepId);
    errors.push({
      code: "INVALID_STEP_CONFIG",
      message: `"${toStep ? nameOf(toStep) : crossing.targetStepId}" runs after "${label}" (Await) but reads directly from "${fromStep ? nameOf(fromStep) : crossing.sourceStepId}", which runs inside the fanned region. Those per-item results only reach this side of the join through the Await — that wire resolves to nothing at run time. Take the value from the Await's output instead.`,
      nodeIds: [fanInStep.id, crossing.sourceStepId, crossing.targetStepId],
    });
    return { errors, enclosingTargetIds: new Set() };
  }

  const mode = resolveFanInMode(fanInStep.config ?? {});

  // `first_*` cannot mean what it says over a BATCHED region. Both modes
  // promise the first child to settle and the cancellation of the rest; the
  // batch runs every item to completion first, so the winner is really the
  // lowest-index eligible item and no losing work is ever called off. The
  // `all_*` modes are unaffected — they are defined over the whole cohort,
  // which is exactly what a batch produces.
  //
  // Refused rather than redefined. A node whose selection rule and cancellation
  // behaviour both change based on a step type's batch config — invisible on the
  // canvas — is worse than a node that says no. Delete this rule if the batched
  // path ever grows a real per-item race.
  if (
    options.isBatchedStepType &&
    boundaryTargetStep &&
    boundaryTargetStepId &&
    options.isBatchedStepType(boundaryTargetStep.type) &&
    isFirstMode(mode)
  ) {
    errors.push({
      code: "INVALID_STEP_CONFIG",
      message: `"${label}" (Await) is set to a "first" mode over "${nameOf(boundaryTargetStep)}", which runs its items batched. A batch finishes every item before the join sees any of them, so nothing is raced and no work is cancelled — the result would just be the lowest-numbered item that qualified. Use "All must succeed" or "All — keep partial results", or fan out from a step that runs its items separately.`,
      nodeIds: [fanInStep.id, boundaryTargetStepId],
    });
    return { errors, enclosingTargetIds: new Set() };
  }

  const expectedResultSocketId = isFirstMode(mode)
    ? FAN_IN_RESULT_SOCKET_ID
    : FAN_IN_RESULTS_SOCKET_ID;

  if (
    !fanInStep.sockets.outputs.some(
      (socket) => socket.id === expectedResultSocketId,
    )
  ) {
    // Name `any` when that is the cause. The resolvers treat an `any` source as
    // unresolved, which is right — but the generic message then reads as
    // "reconnect the wire" on a graph whose wire is connected and correct,
    // sending the author to look at the one thing that is not wrong.
    const valueEdge = edges.find(
      (edge) =>
        edge.targetStepId === fanInStep.id &&
        edge.targetSocketId === FAN_IN_INPUT_SOCKET_ID,
    );
    const sourceSocketType = valueEdge
      ? steps
          .find((candidate) => candidate.id === valueEdge.sourceStepId)
          ?.sockets.outputs.find(
            (socket) => socket.id === valueEdge.sourceSocketId,
          )?.type
      : undefined;

    errors.push({
      code: "INVALID_STEP_CONFIG",
      message:
        sourceSocketType === "any"
          ? `"${label}" (Await) has no result output — the socket wired into its Value input is untyped ("any"), so the element type it would gather cannot be resolved. Wire it from a socket with a concrete type.`
          : `"${label}" (Await) has no result output — the type of the value wired into it could not be resolved. Reconnect its Value input from a typed socket.`,
      nodeIds: [fanInStep.id],
    });
  }

  return { errors, enclosingTargetIds };
}

/**
 * Blocking `fan_in` topology errors for a graph. Pure.
 *
 * Returns an empty array for any graph with no Await nodes, which is every
 * graph that predates the step type.
 */
/**
 * `findFanInTopologyErrors` for a graph check that ALSO runs the ordinary
 * unconnected-required-socket pass.
 *
 * The structural rules stay blocking whatever the caller is doing: a fan-in in
 * the wrong place does not run at all, and no later edit to an unrelated part
 * of the graph makes it valid.
 *
 * The unconnected-Value error is dropped, because every caller of this wrapper
 * already reports that socket the ordinary way. Emitting it here was wrong in
 * both directions: on a draft it rejected an Await the moment it was dropped on
 * the canvas — the one state from which it can still be wired, and unlike every
 * other required input — and at publish it named the same socket twice, since
 * `value` is declared `z.any()`, which `isFieldOptional` reads as REQUIRED.
 *
 * `findFanInTopologyErrors` keeps emitting it for callers that do not run that
 * pass; the message there is the more specific of the two.
 */
export function findFanInSaveErrors(
  steps: ValidationStep[],
  edges: ValidationEdge[],
  options: FanInTopologyOptions = {},
): ValidationError[] {
  return findFanInTopologyErrors(steps, edges, options).filter(
    (error) => error.code !== "UNCONNECTED_REQUIRED_SOCKET",
  );
}

export interface FanInTopologyOptions {
  /**
   * Whether a step TYPE runs its fanout batched, when the caller can tell.
   *
   * Batch config resolves from three places — a hard-coded `stepConfig.batch`,
   * the config schema's own default, and the step instance's config — and only
   * the last is in the persisted graph, so a pure `ValidationStep` cannot
   * answer this. The backend can (`getEffectiveBatchConfig`), and the backend
   * is the gate every save path runs through. Omitted means the batched rule is
   * skipped rather than guessed at.
   */
  isBatchedStepType?: (stepType: string) => boolean;
}

export function findFanInTopologyErrors(
  steps: ValidationStep[],
  edges: ValidationEdge[],
  options: FanInTopologyOptions = {},
): ValidationError[] {
  const fanInSteps = steps.filter(isFanInValidationStep);
  if (fanInSteps.length === 0) return [];

  // Reuse the fanout analyser rather than re-deriving regions: it already
  // mirrors the engine's `remainingStepIds` seeding, including the
  // expression-vs-stored-edge subtleties that decide which subgraph a child
  // actually walks.
  const findings = analyseFanoutTopology(steps, edges);

  // The set the engine walks. `analyseFanoutTopology` resolves this internally
  // for its own traversal; the detached-reachability check below needs the same
  // one, so it is resolved here rather than read off a finding.
  const traversalEdges = resolveActiveEdges(
    steps.map((step) => ({ id: step.id, inputSockets: step.sockets.inputs })),
    edges,
  );

  const errors: ValidationError[] = [];
  /** Fanout target -> the Await steps that join it. See the region check below. */
  const awaitsByRegion = new Map<string, Set<string>>();

  for (const fanInStep of fanInSteps) {
    const result = checkOneFanIn(
      fanInStep,
      steps,
      edges,
      traversalEdges,
      findings,
      options,
    );
    errors.push(...result.errors);

    // Record the pairing for the region-side check below. The rule is
    // one-to-one, and testing it per-Await only proves one direction.
    for (const boundaryTargetId of result.enclosingTargetIds) {
      const joins = awaitsByRegion.get(boundaryTargetId) ?? new Set<string>();
      joins.add(fanInStep.id);
      awaitsByRegion.set(boundaryTargetId, joins);
    }
  }

  // The other direction of the same rule. Checking each Await independently
  // only proves a join has one region; a region that BRANCHES into two Awaits
  // gives each of them exactly one enclosing region, so both pass and the
  // definition publishes. At runtime `collectFannedRegion` then discovers both
  // joins in one traversal and `resolveFanInJoin` throws, because a cohort has
  // no way to be gathered by two barriers. Rejecting it here turns a failed run
  // into a design-time message.
  for (const [boundaryTargetId, joins] of awaitsByRegion) {
    if (joins.size <= 1) continue;
    const joinLabels = [...joins]
      .map((id) => {
        const joinStep = steps.find((candidate) => candidate.id === id);
        return `"${joinStep ? nameOf(joinStep) : id}"`;
      })
      .join(", ");
    errors.push({
      code: "INVALID_STEP_CONFIG",
      message: `One fanned-out region is joined by ${joins.size} Await steps (${joinLabels}), so its cohort has no single barrier to gather it. Merge them into one Await, or split the region so each branch fans out separately.`,
      nodeIds: [boundaryTargetId, ...joins],
    });
  }

  return errors;
}
